import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { generatePresignedUrls } from '../../actions/files/generatePresignedUrls';
import { createRafExtract } from '../../actions/matters/createRafExtract';
import { getMatter } from '../../actions/matters/getMatter';

interface SourceFile {
  key: string;
  filename: string;
  uploadedAt: string;
}

interface SourceDocumentsPanelProps {
  matterId: string;
  sourceFiles: SourceFile[] | undefined;
  onExtractionComplete: () => void;
}

const ACCEPTED_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
const ACCEPTED_EXT = '.pdf,.docx,.jpg,.jpeg,.png';
const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 30;

const SourceDocumentsPanel: React.FC<SourceDocumentsPanelProps> = ({
  matterId,
  sourceFiles,
  onExtractionComplete,
}) => {
  const [showUpload, setShowUpload] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((newFiles: File[]) => {
    setFileError('');
    const oversized = newFiles.find((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized) {
      setFileError(`${oversized.name} exceeds ${MAX_FILE_SIZE_MB} MB limit.`);
      return;
    }
    setFiles((prev) => {
      const combined = [...prev, ...newFiles].slice(0, MAX_FILES);
      if (prev.length + newFiles.length > MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files allowed.`);
      }
      return combined;
    });
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError('');
  };

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter((f) => ACCEPTED_TYPES.includes(f.type));
    addFiles(dropped);
  }, [addFiles]);

  const handleAdditiveExtract = async () => {
    if (files.length === 0) return;
    setExtracting(true);
    setExtractProgress(10);
    setFileError('');

    try {
      const presignedData = await generatePresignedUrls(
        files.map((f) => ({ filename: f.name, contentType: f.type, size: f.size })),
      );
      setExtractProgress(30);

      await Promise.all(
        presignedData.map(async (p, i) => {
          await fetch(p.url, {
            method: 'PUT',
            body: files[i],
            headers: { 'Content-Type': files[i].type },
          });
        }),
      );
      setExtractProgress(60);

      const fileKeys = presignedData.map((p) => p.key);
      const newFileMetadata = presignedData.map((p, i) => ({
        key: p.key,
        filename: files[i].name,
      }));

      await createRafExtract(matterId, fileKeys, false, {
        isAdditive: true,
        newFileMetadata,
      });
      setExtractProgress(80);
      setFiles([]);

      const maxAttempts = 60;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const updated = await getMatter(matterId);
          const newCount = updated.sourceFiles?.length || 0;
          const oldCount = sourceFiles?.length || 0;
          if (newCount > oldCount) {
            setShowUpload(false);
            setExtracting(false);
            setExtractProgress(0);
            onExtractionComplete();
            return;
          }
          setExtractProgress(80 + Math.min(attempt * 0.3, 15));
        } catch {
          // Ignore transient fetch errors, keep polling
        }
      }
      setShowUpload(false);
      setExtracting(false);
      setExtractProgress(0);
      onExtractionComplete();
    } catch (err: any) {
      setFileError(err?.message || 'Extraction failed.');
      setExtracting(false);
      setExtractProgress(0);
    }
  };

  const docs = sourceFiles || [];

  return (
    <Box
      sx={{
        bgcolor: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'var(--t3)' }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--t1)', letterSpacing: '0.005em' }}>
            Source Documents
          </Typography>
          {docs.length > 0 && (
            <Box
              sx={{
                bgcolor: 'var(--s2)',
                color: 'var(--t2)',
                fontSize: '0.66rem',
                fontWeight: 700,
                px: 0.625,
                py: 0.05,
                borderRadius: '6px',
                minWidth: 18,
                textAlign: 'center',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {docs.length}
            </Box>
          )}
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
          onClick={() => setShowUpload(!showUpload)}
          disabled={extracting}
          sx={{
            color: 'var(--teal)',
            fontSize: '0.72rem',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: 'auto',
            px: 0.875,
            py: 0.25,
            borderRadius: '6px',
            '&:hover': { bgcolor: 'color-mix(in srgb, var(--teal) 8%, transparent)' },
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Document list */}
      <Box sx={{ px: 1.75, py: 1.25 }}>
        {docs.length === 0 && !showUpload && (
          <Typography sx={{ color: 'var(--t4)', fontSize: '0.76rem', textAlign: 'center', py: 1.5 }}>
            No documents uploaded yet
          </Typography>
        )}
        {docs.map((doc, i) => (
          <Box
            key={doc.key + i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.875,
              py: 0.75,
              borderBottom: i < docs.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <InsertDriveFileIcon sx={{ fontSize: 14, color: 'var(--t3)', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.76rem',
                  color: 'var(--t1)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.35,
                  fontWeight: 500,
                }}
              >
                {doc.filename}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: 'var(--t4)', lineHeight: 1.35, fontFamily: 'ui-monospace, monospace' }}>
                {new Date(doc.uploadedAt).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Upload section */}
      {showUpload && (
        <Box sx={{ px: 1.75, pb: 1.75, borderTop: '1px solid var(--border)' }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'var(--warn)', fontWeight: 500, mt: 1.25, mb: 1 }}>
            Save any changes before adding new documents
          </Typography>

          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '1.5px dashed var(--border)',
              borderRadius: '8px',
              p: 1.75,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': { borderColor: 'var(--teal)', bgcolor: 'var(--s1)' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 24, color: 'var(--t3)', mb: 0.5 }} />
            <Typography sx={{ color: 'var(--t1)', fontSize: '0.74rem', fontWeight: 500 }}>
              Drop files or click to browse
            </Typography>
            <Typography sx={{ color: 'var(--t4)', fontSize: '0.66rem', mt: 0.25 }}>
              PDF, DOCX, JPG, PNG
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={ACCEPTED_EXT}
              onChange={(e) => {
                if (e.target.files) addFiles(Array.from(e.target.files));
                e.target.value = '';
              }}
            />
          </Box>

          {files.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {files.map((f, i) => (
                <Box
                  key={`new-${f.name}-${i}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 0.875,
                    py: 0.5,
                    bgcolor: 'var(--s1)',
                    borderRadius: '6px',
                    border: '1px solid var(--border)',
                  }}
                >
                  <InsertDriveFileIcon sx={{ fontSize: 12, color: 'var(--t3)' }} />
                  <Typography sx={{ flex: 1, fontSize: '0.72rem', color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {f.name}
                  </Typography>
                  <IconButton size="small" onClick={() => removeFile(i)} sx={{ p: 0.25, color: 'var(--t4)', '&:hover': { color: 'var(--err)' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {fileError && (
            <Typography sx={{ color: 'var(--err)', fontSize: '0.72rem', mt: 0.75 }}>{fileError}</Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            disabled={files.length === 0 || extracting}
            onClick={handleAdditiveExtract}
            startIcon={extracting ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <AddIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              mt: 1.5,
              bgcolor: 'var(--teal)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.76rem',
              textTransform: 'none',
              borderRadius: '8px',
              py: 0.625,
              boxShadow: 'none',
              '&:hover': { bgcolor: 'var(--teal-mid, #2c3578)', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: 'var(--s2)', color: 'var(--t4)' },
            }}
          >
            {extracting ? 'Extracting...' : 'Extract & Merge'}
          </Button>

          {extracting && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={extractProgress}
                sx={{
                  height: 3,
                  borderRadius: 2,
                  bgcolor: 'var(--s2)',
                  '& .MuiLinearProgress-bar': { bgcolor: 'var(--teal)', borderRadius: 2 },
                }}
              />
              <Typography sx={{ color: 'var(--t3)', fontSize: '0.66rem', mt: 0.5, textAlign: 'center' }}>
                {extractProgress < 30 ? 'Preparing...' : extractProgress < 60 ? 'Uploading...' : extractProgress < 80 ? 'Extracting & merging...' : 'Processing... this may take a few minutes'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SourceDocumentsPanel;
